/**
 * Shared helpers for the "download the app" web prompts (banner + modal).
 *
 * The audience is genuine mobile-web users (iOS/Android browsers) — the people
 * most likely arriving from an external-job push/notification link who'd be
 * better served in the native app. Desktop users are never prompted.
 */

export const APP_STORE_URL =
  'https://apps.apple.com/us/app/find-my-filmmaker/id6752275597';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.fmfexpo.findmyfilmmaker&hl=en_GB';

export type MobilePlatform = 'ios' | 'android' | 'desktop';

export function getMobilePlatform(): MobilePlatform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) && !/windows/.test(ua);
  if (isIOS) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
}

/** True only on an actual iOS/Android browser — the app-promo audience. */
export function isMobileWeb(): boolean {
  return getMobilePlatform() !== 'desktop';
}

export function getStoreUrl(platform: MobilePlatform = getMobilePlatform()): string {
  return platform === 'android' ? PLAY_STORE_URL : APP_STORE_URL;
}

/**
 * Build a deep link into the native app. Mirrors the scheme used by
 * JobRedirect.tsx (exp+ prefix for local dev builds).
 */
export function buildAppDeepLink(path = ''): string {
  const isDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname.startsWith('192.168'));
  const scheme = isDev ? 'exp+findmyfilmmaker://' : 'findmyfilmmaker://';
  return `${scheme}${path.replace(/^\//, '')}`;
}

// ---- Dismissal / one-time persistence -------------------------------------

const BANNER_DISMISSED_KEY = 'fmf_app_banner_dismissed_at';
const MODAL_SEEN_KEY = 'fmf_app_modal_seen_at';
// Re-show the slim banner after this long so it isn't gone forever.
const BANNER_SNOOZE_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function readTs(key: string): number | null {
  try {
    const v = localStorage.getItem(key);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

function writeTs(key: string, ts: number): void {
  try {
    localStorage.setItem(key, String(ts));
  } catch {
    /* storage unavailable — fail open */
  }
}

export function isBannerDismissed(now = Date.now()): boolean {
  const ts = readTs(BANNER_DISMISSED_KEY);
  return ts !== null && now - ts < BANNER_SNOOZE_MS;
}

export function dismissBanner(now = Date.now()): void {
  writeTs(BANNER_DISMISSED_KEY, now);
}

/** The modal is one-time per device (until storage is cleared). */
export function hasSeenAppModal(): boolean {
  return readTs(MODAL_SEEN_KEY) !== null;
}

export function markAppModalSeen(now = Date.now()): void {
  writeTs(MODAL_SEEN_KEY, now);
}
